import React, { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { argus, Btn, Label, inputCls } from "@/components/argus/ui";
import ArgusSearch from "@/components/argus/ArgusSearch";
import { WhipMoney, WhipTimeline, WhipEvidence, WhipProof, WhipPacket } from "@/components/argus/WhipParts";

const TABS = [["report", "Report"], ["money", "Money"], ["timeline", "Timeline"], ["evidence", "Evidence"], ["proof", "Proof"], ["search", "Records search"], ["packet", "Packet"]];
const PROGRAMS = [["whip", "DOJ WHIP (fraud.gov): health care, benefits, SBA, Labor, USDA, Education"], ["sec", "SEC: securities and investor fraud (10–30% if over $1M)"], ["cftc", "CFTC: commodities and crypto derivatives fraud"], ["irs", "IRS Form 211: tax fraud over $2M"], ["fca", "False Claims Act: fraud on government contracts (needs a lawyer)"], ["other", "Not sure yet"]];
const FIELDS = [
  ["subjects", "Who is involved", "Names of people, companies, roles, addresses you know", 3],
  ["federal_program", "Which federal program or money", "e.g. Medicare, SNAP, PPP loan, unemployment, federal grant", 1],
  ["what", "What happened", "Describe the scheme step by step, plainly. Facts, not opinions.", 7],
  ["where", "Where", "City, state, offices, websites", 1],
  ["when_text", "When", "When it started, whether it is still happening", 1],
  ["how_known", "How you know", "Your role and how you saw it. Never include anything you obtained illegally.", 3],
];

export default function WhipReport({ rid, onUpdated, onDeleted }) {
  const [r, setR] = useState(null); const [tab, setTab] = useState("report");
  useEffect(() => { setR(null); argus.get(`/whip/reports/${rid}`).then(x => setR(x.data)); }, [rid]);
  if (r && r.report_id !== rid) return null;
  const patch = async (b) => { const { data } = await argus.patch(`/whip/reports/${rid}`, b); setR(data); onUpdated(data); };
  if (!r) return <div className="p-8 text-white/40 text-sm">Loading report…</div>;
  return (
    <div className="p-5 sm:p-8 space-y-5" data-testid="whip-report">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="basis-full sm:basis-auto flex-1 min-w-0">
          <Label>Fraud report · started {new Date(r.created_at).toLocaleDateString()}</Label>
          <input className="mt-1 w-full bg-transparent argus-display text-3xl font-bold focus:outline-none" defaultValue={r.title} onBlur={e => e.target.value.trim().length > 1 && e.target.value.trim() !== r.title && patch({ title: e.target.value.trim() })} data-testid="whip-title" />
        </div>
        <Btn kind="danger" onClick={async () => { if (window.confirm("Delete this report?")) { await argus.del(`/whip/reports/${rid}`); onDeleted(rid); } }} data-testid="whip-delete"><Trash2 className="w-3.5 h-3.5" /> Delete</Btn>
      </div>
      <div className="flex gap-1 flex-wrap border-b border-white/10 pb-2" data-testid="whip-tabs">
        {TABS.map(([k, l]) => <button key={k} type="button" onClick={() => setTab(k)} className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${tab === k ? "bg-amber-300 text-black" : "text-white/60 hover:text-white"}`} data-testid={`whip-tab-${k}`}>{l}</button>)}
      </div>
      {tab === "report" && (
        <div className="space-y-4 max-w-3xl">
          <div><Label>Where to report</Label>
            <select className={`${inputCls} mt-1`} value={r.program} onChange={e => patch({ program: e.target.value })} data-testid="whip-program">{PROGRAMS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
          {FIELDS.map(([k, l, ph, rows]) => (
            <div key={k}><Label>{l}</Label>
              {rows > 1 ? <textarea rows={rows} className={`${inputCls} mt-1`} defaultValue={r[k]} placeholder={ph} onBlur={e => e.target.value !== r[k] && patch({ [k]: e.target.value })} data-testid={`whip-field-${k}`} />
                : <input className={`${inputCls} mt-1`} defaultValue={r[k]} placeholder={ph} onBlur={e => e.target.value !== r[k] && patch({ [k]: e.target.value })} data-testid={`whip-field-${k}`} />}
            </div>
          ))}
          <p className="text-[11px] text-white/40">Saves automatically when you leave a field.</p>
        </div>
      )}
      {tab === "money" && <WhipMoney r={r} patch={patch} />}
      {tab === "timeline" && <WhipTimeline r={r} patch={patch} />}
      {tab === "evidence" && <WhipEvidence r={r} setR={setR} />}
      {tab === "proof" && <WhipProof r={r} setR={setR} />}
      {tab === "search" && <div className="-m-5 md:-m-8"><ArgusSearch cases={[]} endpoint="/whip/search" /></div>}
      {tab === "packet" && <WhipPacket r={r} programs={PROGRAMS} />}
    </div>
  );
}
